/**
 * The audio graph: context, buses, master limiter, shared reverb, ducking, 3D listener.
 *
 *   sfx ──┐                       ┌─ reverb (convolver) ─┐
 *   music ┼─ bus gains ─ [duck] ──┼──────────────────────┼─ master gain ─ compressor ─ soft clip ─ out
 *   voice ┘                       └──────────────────────┘
 */
import type { Vector3 } from 'three';
import { clamp, makeHeadroomClipCurve, makeImpulseResponse, type NoiseColor } from './dsp';
import { noiseFor } from './kit';

export interface Volumes {
  master: number;
  music: number;
  sfx: number;
  voice: number;
}

export class AudioGraph {
  readonly ctx: AudioContext;
  readonly master: GainNode;
  readonly music: GainNode;
  readonly sfx: GainNode;
  readonly voice: GainNode;
  /** Music ducking gain (1 = no duck). Sits after the music volume gain. */
  readonly musicDuck: GainNode;
  readonly reverb: ConvolverNode;
  readonly reverbReturn: GainNode;
  /** Send into the shared reverb for SFX (scaled with the sfx volume so muting sfx mutes its tail). */
  readonly sfxReverbSend: GainNode;
  readonly compressor: DynamicsCompressorNode;
  readonly analyser: AnalyserNode;
  readonly isMobile: boolean;
  private volumes: Volumes = { master: 0.8, music: 0.7, sfx: 0.9, voice: 0.9 };
  private duckUntil = 0;
  private duckDepth = 1;
  private duckTimer = 0;
  private hiddenGain = 1;

  constructor(ctx: AudioContext) {
    this.ctx = ctx;
    this.isMobile = /Android|iPhone|iPad|iPod|Mobile/i.test(navigator.userAgent) || (navigator.hardwareConcurrency ?? 8) <= 4;

    this.compressor = ctx.createDynamicsCompressor();
    this.compressor.threshold.value = -12;
    this.compressor.knee.value = 18;
    this.compressor.ratio.value = 6;
    this.compressor.attack.value = 0.004;
    this.compressor.release.value = 0.18;

    // Fast peak limiter after the glue compressor, then a soft clipper with 6 dB of input
    // headroom (pre-gain 0.5, curve spans ±2) and a -0.35 dBFS ceiling so stacked hits never
    // hard-clip.
    const limiter = ctx.createDynamicsCompressor();
    limiter.threshold.value = -4;
    limiter.knee.value = 3;
    limiter.ratio.value = 20;
    limiter.attack.value = 0.001;
    limiter.release.value = 0.1;
    const pre = ctx.createGain();
    pre.gain.value = 0.5;
    const clip = ctx.createWaveShaper();
    clip.curve = makeHeadroomClipCurve();
    clip.oversample = '2x';

    this.master = ctx.createGain();
    this.master.connect(this.compressor).connect(limiter).connect(pre).connect(clip).connect(ctx.destination);

    this.analyser = ctx.createAnalyser();
    this.analyser.fftSize = 512;
    this.analyser.smoothingTimeConstant = 0.6;
    clip.connect(this.analyser);

    this.music = ctx.createGain();
    this.musicDuck = ctx.createGain();
    this.music.connect(this.musicDuck).connect(this.master);
    this.sfx = ctx.createGain();
    this.sfx.connect(this.master);
    this.voice = ctx.createGain();
    this.voice.connect(this.master);

    // Shared reverb (shorter IR on mobile to save CPU).
    this.reverb = ctx.createConvolver();
    this.reverb.buffer = makeImpulseResponse(ctx, this.isMobile ? 1.6 : 2.6, 3.2, 0.5);
    this.reverbReturn = ctx.createGain();
    this.reverbReturn.gain.value = 0.8;
    this.reverb.connect(this.reverbReturn).connect(this.master);
    this.sfxReverbSend = ctx.createGain();
    this.sfxReverbSend.connect(this.reverb);

    this.applyVolumes();
  }

  get now(): number {
    return this.ctx.currentTime;
  }

  noiseBuffer(color: NoiseColor): AudioBuffer {
    return noiseFor(this.ctx, color);
  }

  setVolumes(v: Volumes): void {
    this.volumes = { ...v };
    this.applyVolumes();
  }

  getVolumes(): Volumes {
    return { ...this.volumes };
  }

  /** Global gain applied when the tab is hidden (fade rather than a hard cut). */
  setHidden(hidden: boolean): void {
    this.hiddenGain = hidden ? 0 : 1;
    this.applyVolumes(hidden ? 0.15 : 0.4);
  }

  private applyVolumes(tc = 0.04): void {
    const t = this.ctx.currentTime;
    const v = this.volumes;
    this.master.gain.setTargetAtTime(clamp(v.master, 0, 1) * this.hiddenGain, t, tc);
    this.music.gain.setTargetAtTime(clamp(v.music, 0, 1) * 0.7, t, tc);
    this.sfx.gain.setTargetAtTime(clamp(v.sfx, 0, 1) * 0.8, t, tc);
    this.sfxReverbSend.gain.setTargetAtTime(clamp(v.sfx, 0, 1) * 0.8, t, tc);
    this.voice.gain.setTargetAtTime(clamp(v.voice, 0, 1) * 1.0, t, tc);
  }

  /** Lower music to (1 - amount) for `seconds`, then recover smoothly. Overlapping ducks keep the deeper/longer one. */
  duck(amount: number, seconds: number): void {
    const t = this.ctx.currentTime;
    const active = t < this.duckUntil;
    const depth = active ? Math.min(this.duckDepth, clamp(1 - amount, 0, 1)) : clamp(1 - amount, 0, 1);
    this.duckDepth = depth;
    this.duckUntil = Math.max(active ? this.duckUntil : 0, t + Math.max(0.05, seconds));
    const g = this.musicDuck.gain;
    g.cancelScheduledValues(t);
    g.setTargetAtTime(depth, t, 0.08);
    clearTimeout(this.duckTimer);
    this.duckTimer = window.setTimeout(() => {
      const t2 = this.ctx.currentTime;
      g.cancelScheduledValues(t2);
      g.setTargetAtTime(1, t2, 0.35);
      this.duckUntil = 0;
      this.duckDepth = 1;
    }, (this.duckUntil - t) * 1000);
  }

  // ---- 3D listener -------------------------------------------------------

  private lastListener = { x: 0, y: 0, z: 0 };

  setListener(position: Vector3, forward: Vector3): void {
    const l = this.ctx.listener;
    const fx = forward.x, fy = forward.y, fz = forward.z;
    // Up vector: world up orthogonalised against forward (fallback when looking straight down).
    let ux = 0 - fx * fy, uy = 1 - fy * fy, uz = 0 - fz * fy;
    let len = Math.hypot(ux, uy, uz);
    if (len < 1e-3) {
      ux = 0; uy = 0; uz = -1;
      len = 1;
    }
    ux /= len; uy /= len; uz /= len;
    this.lastListener = { x: position.x, y: position.y, z: position.z };
    const t = this.ctx.currentTime;
    if (l.positionX) {
      l.positionX.setTargetAtTime(position.x, t, 0.02);
      l.positionY.setTargetAtTime(position.y, t, 0.02);
      l.positionZ.setTargetAtTime(position.z, t, 0.02);
      l.forwardX.setTargetAtTime(fx, t, 0.02);
      l.forwardY.setTargetAtTime(fy, t, 0.02);
      l.forwardZ.setTargetAtTime(fz, t, 0.02);
      l.upX.setTargetAtTime(ux, t, 0.02);
      l.upY.setTargetAtTime(uy, t, 0.02);
      l.upZ.setTargetAtTime(uz, t, 0.02);
    } else {
      // Legacy Safari
      (l as any).setPosition?.(position.x, position.y, position.z);
      (l as any).setOrientation?.(fx, fy, fz, ux, uy, uz);
    }
  }

  distanceToListener(p: { x: number; y: number; z: number }): number {
    const l = this.lastListener;
    return Math.hypot(p.x - l.x, p.y - l.y, p.z - l.z);
  }

  /** Create a panner for a world position; returns the node to connect the sound into. */
  spatial(position: { x: number; y: number; z: number }, out: AudioNode): AudioNode {
    const p = this.ctx.createPanner();
    p.panningModel = 'equalpower';
    p.distanceModel = 'inverse';
    p.refDistance = 12;
    p.maxDistance = 2000;
    p.rolloffFactor = 0.55;
    p.coneOuterGain = 1;
    const t = this.ctx.currentTime;
    if (p.positionX) {
      p.positionX.setValueAtTime(position.x, t);
      p.positionY.setValueAtTime(position.y, t);
      p.positionZ.setValueAtTime(position.z, t);
    } else {
      (p as any).setPosition?.(position.x, position.y, position.z);
    }
    p.connect(out);
    return p;
  }
}

/** Create the AudioContext (suspended is fine; unlock() resumes it). */
export function createContext(): AudioContext | undefined {
  try {
    const Ctx = window.AudioContext || (window as any).webkitAudioContext;
    if (!Ctx) return undefined;
    return new Ctx({ latencyHint: 'interactive' });
  } catch {
    return undefined;
  }
}
